import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine, ReferenceDot,
} from "recharts";
import { TrendingUp, TrendingDown, Flame, Trophy, Target, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Dramatic weight progress chart with weekly / monthly view + live WS updates.
 *
 * Props:
 *   - userId: optional — when set, fetches /trainer/clients/{userId}/body-weights
 *     (trainer view, READ-ONLY: no Add input). When unset, this is the owner
 *     viewing their own chart via /body-weights.
 *
 * Live updates:
 *   - Subscribes to `weight:updated` WS events fanned out from the backend.
 *     Any time the owner (or anyone they're connected with) logs/deletes a
 *     weight, every connected device updates in <1s.
 *
 * View modes (`weekly` | `monthly`):
 *   - Weekly: bucket-average per ISO week, plotted across the full range.
 *     Smoother, hides daily noise. Best for spotting trend over months.
 *   - Monthly: bucket-average per calendar month. Best for long-term/year
 *     -over-year view.
 *   - Both modes always include the user's most recent raw datapoint as a
 *     "live now" marker so today's just-logged weight pops on the chart
 *     even if it's mid-week.
 */
export default function WeightTrackerChart({ userId = null, readOnly = false }) {
  const { subscribe } = useWebSocket();
  const { user: authUser, setUser } = useAuth();
  const [weights, setWeights] = useState([]);
  const [clientGoal, setClientGoal] = useState(null); // populated only in trainer view
  const [newWeight, setNewWeight] = useState("");
  const [view, setView] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justLoggedAt, setJustLoggedAt] = useState(null);
  const [goalEditing, setGoalEditing] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [goalSaving, setGoalSaving] = useState(false);
  // Tap-to-zoom: we replace Recharts' default Tooltip content with a rich
  // card that shows weight + date + delta-from-prev + distance-to-goal.
  // Recharts already handles the tap-to-stick behavior on touch devices
  // (the tooltip persists until the user taps elsewhere) — we just upgrade
  // its visual content here. No custom click/pin machinery needed.
  const endpoint = userId ? `/trainer/clients/${userId}/body-weights` : `/body-weights`;

  // Goal weight comes from auth.user for own chart, from trainer endpoint for client view.
  const goalWeight = userId ? clientGoal : (authUser?.goal_weight_kg ?? null);
  const canEditGoal = !userId && !readOnly;

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      // Trainer endpoint returns {entries, goal_weight_kg}; own endpoint returns an array.
      if (Array.isArray(data)) {
        setWeights(data);
      } else {
        setWeights(data?.entries || []);
        setClientGoal(typeof data?.goal_weight_kg === "number" ? data.goal_weight_kg : null);
      }
    } catch {
      setWeights([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  // Live updates — listen for any weight change on the user we're viewing.
  useEffect(() => {
    const unsub = subscribe("weight:updated", (msg) => {
      // Owner view: msg.user_id should match the current user (still gate to be safe).
      // Trainer view: msg.user_id must match the client we're looking at.
      const target = userId; // null = self
      if (target && msg.user_id !== target) return;
      // Optimistic merge — no full refetch needed. Falls back to load() if
      // the payload is missing the new entry shape (defensive).
      if (msg.action === "add" && msg.entry) {
        setWeights((prev) => prev.some((w) => w.entry_id === msg.entry.entry_id) ? prev : [...prev, msg.entry]);
        // Pulse the "just logged" marker for 4s so trainers see WHICH point is new.
        setJustLoggedAt(msg.entry.date);
        setTimeout(() => setJustLoggedAt(null), 4000);
      } else if (msg.action === "delete" && msg.entry_id) {
        setWeights((prev) => prev.filter((w) => w.entry_id !== msg.entry_id));
      } else {
        load();
      }
    });
    return () => unsub && unsub();
    // eslint-disable-next-line
  }, [subscribe, userId]);

  const saveGoal = async (clear = false) => {
    setGoalSaving(true);
    try {
      const payload = clear ? { goal_weight_kg: 0 } : { goal_weight_kg: Number(goalInput) };
      if (!clear && (!payload.goal_weight_kg || payload.goal_weight_kg <= 0)) {
        toast.error("Enter a positive number");
        setGoalSaving(false);
        return;
      }
      const { data } = await api.put("/me", payload);
      // AuthContext exposes setUser; merge the fresh user so goal_weight_kg reflects everywhere instantly.
      if (data && setUser) setUser(data);
      toast.success(clear ? "Goal cleared" : `Goal set to ${payload.goal_weight_kg} kg`);
      setGoalEditing(false);
      setGoalInput("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save goal");
    } finally {
      setGoalSaving(false);
    }
  };

  const addWeight = async (e) => {
    e.preventDefault();
    const n = Number(newWeight);
    if (!n || n <= 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/body-weights", { weight_kg: n });
      setWeights((ws) => [...ws, data]);
      setJustLoggedAt(data.date);
      setTimeout(() => setJustLoggedAt(null), 4000);
      setNewWeight("");
      toast.success(`Logged ${n} kg`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't log");
    } finally {
      setSaving(false);
    }
  };

  // --- Aggregations ---
  const sorted = useMemo(() => {
    // Stable sort: by date asc, then by created_at asc for same-day entries
    // so multiple logs on the same day plot in the order they were recorded.
    return [...weights].sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
  }, [weights]);

  // Plot one dot per real weight entry within the selected window. The line
  // now rises and falls with every actual log (no bucket-averaging), so the
  // user sees their true progress relative to the goal line.
  //   - weekly:  last 7 days (from today, inclusive)
  //   - monthly: last 30 days (from today, inclusive)
  const chartData = useMemo(() => {
    if (sorted.length === 0) return [];
    const days = view === "weekly" ? 7 : 30;
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - (days - 1));
    const cutoffISO = cutoff.toISOString().slice(0, 10);
    const filtered = sorted.filter((w) => w.date >= cutoffISO);
    return filtered.map((w, i) => {
      const prev = i === 0 ? null : filtered[i - 1];
      const deltaKg = prev ? Number((Number(w.weight_kg) - Number(prev.weight_kg)).toFixed(1)) : null;
      return {
        // entry_id is the unique X key — guarantees stable rendering even if
        // multiple weights are logged on the same calendar date.
        key: w.entry_id || `${w.date}-${w.weight_kg}`,
        label: w.date.slice(5), // MM-DD
        weight: Number(w.weight_kg),
        date: w.date,
        deltaKg, // ± kg from the previous plotted entry (null for first dot)
        entry_id: w.entry_id,
        created_at: w.created_at,
      };
    });
  }, [sorted, view]);

  // --- Insights for the dramatic header strip ---
  const insights = useMemo(() => {
    if (sorted.length === 0) return null;
    const latest = sorted[sorted.length - 1];
    const first = sorted[0];
    const diff = Number((latest.weight_kg - first.weight_kg).toFixed(1));
    const minPoint = sorted.reduce((m, x) => (x.weight_kg < m.weight_kg ? x : m), sorted[0]);
    // Past 30 days slice
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffISO = cutoff.toISOString().slice(0, 10);
    const recent = sorted.filter((x) => x.date >= cutoffISO);
    const recentDiff = recent.length >= 2
      ? Number((recent[recent.length - 1].weight_kg - recent[0].weight_kg).toFixed(1))
      : null;
    return {
      latest: latest.weight_kg,
      latestDate: latest.date,
      totalDiff: diff,
      lowest: minPoint.weight_kg,
      lowestDate: minPoint.date,
      recentDiff,
      toGoal: goalWeight != null ? Number((latest.weight_kg - goalWeight).toFixed(1)) : null,
    };
  }, [sorted, goalWeight]);

  // Domain padding so the line doesn't kiss the chart edge.
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 100];
    const vals = chartData.map((d) => d.weight);
    let min = Math.min(...vals);
    let max = Math.max(...vals);
    // Stretch the domain to include the goal line so it's always visible.
    if (goalWeight != null && goalWeight > 0) {
      min = Math.min(min, goalWeight);
      max = Math.max(max, goalWeight);
    }
    const pad = Math.max(2, (max - min) * 0.25);
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [chartData, goalWeight]);

  // Find the recharts datapoint that maps to the just-logged entry so we can
  // pulse a "live now" ring on it. The XAxis uses `label` as its dataKey, so
  // we return the matching label. For same-day double-logs we pick the most
  // recent one (rare edge case — the dot still lands on the right column).
  const liveMarkerKey = useMemo(() => {
    if (!justLoggedAt) return null;
    const match = [...chartData].reverse().find((d) => d.date === justLoggedAt);
    return match?.label || null;
  }, [chartData, justLoggedAt]);

  const trendDown = insights?.recentDiff != null && insights.recentDiff < 0;
  const trendUp = insights?.recentDiff != null && insights.recentDiff > 0;

  return (
    <div data-testid="weight-tracker">
      {/* Dramatic insight strip — gives the chart context without needing to read it */}
      {insights && (
        <div data-testid="weight-insights" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <InsightCell label="Current" value={`${insights.latest}`} unit="kg" />
          <InsightCell
            label="Since start"
            value={`${insights.totalDiff > 0 ? "+" : ""}${insights.totalDiff}`}
            unit="kg"
            tone={insights.totalDiff < 0 ? "down" : insights.totalDiff > 0 ? "up" : "flat"}
          />
          <InsightCell
            label="Last 30d"
            value={insights.recentDiff != null ? `${insights.recentDiff > 0 ? "+" : ""}${insights.recentDiff}` : "—"}
            unit="kg"
            tone={trendDown ? "down" : trendUp ? "up" : "flat"}
          />
          {insights.toGoal != null ? (
            <InsightCell
              label="To goal"
              value={`${insights.toGoal > 0 ? "-" : insights.toGoal < 0 ? "+" : ""}${Math.abs(insights.toGoal)}`}
              unit="kg"
              tone={insights.toGoal === 0 ? "goal" : insights.toGoal > 0 ? "down" : "up"}
              icon={Target}
              testid="insight-to-goal"
            />
          ) : (
            <InsightCell label="Lowest" value={`${insights.lowest}`} unit="kg" icon={Trophy} />
          )}
        </div>
      )}

      {/* Add weight (only for own chart) */}
      {!readOnly && !userId && (
        <form onSubmit={addWeight} className="flex gap-2 items-end mb-3" data-testid="weight-log-form">
          <div className="flex-1">
            <Label className="text-xs tracking-widest uppercase text-neutral-400">Add today's weight (kg)</Label>
            <Input
              data-testid="bw-input"
              type="number"
              min={0}
              step={0.1}
              value={newWeight}
              onChange={(e) => setNewWeight(e.target.value)}
              className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
            />
          </div>
          <Button
            data-testid="bw-submit"
            type="submit"
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Log
          </Button>
        </form>
      )}

      {/* View toggle + live badge + goal control */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="inline-flex border border-neutral-800" role="tablist" data-testid="weight-view-toggle">
          <button
            data-testid="weight-view-weekly"
            onClick={() => setView("weekly")}
            className={`px-3 h-8 text-xs uppercase tracking-widest font-display ${view === "weekly" ? "bg-blue-500 text-white" : "text-neutral-400 hover:text-white"}`}
          >
            Weekly
          </button>
          <button
            data-testid="weight-view-monthly"
            onClick={() => setView("monthly")}
            className={`px-3 h-8 text-xs uppercase tracking-widest font-display ${view === "monthly" ? "bg-blue-500 text-white" : "text-neutral-400 hover:text-white"}`}
          >
            Monthly
          </button>
        </div>
        <div className="flex items-center gap-2">
          {liveMarkerKey && (
            <div data-testid="weight-live-badge" className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-blue-500 font-bold">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              Just logged
            </div>
          )}
          {canEditGoal && !goalEditing && (
            <button
              data-testid="weight-goal-btn"
              onClick={() => {
                setGoalInput(goalWeight != null ? String(goalWeight) : "");
                setGoalEditing(true);
              }}
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-display text-emerald-400 hover:text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 px-2.5 h-8"
            >
              <Target className="w-3 h-3" />
              {goalWeight != null ? `Goal ${goalWeight} kg` : "Set goal"}
            </button>
          )}
        </div>
      </div>

      {/* Inline goal editor */}
      {canEditGoal && goalEditing && (
        <div data-testid="weight-goal-editor" className="flex items-end gap-2 mb-3 border border-emerald-500/30 bg-emerald-500/5 p-2.5">
          <div className="flex-1">
            <Label className="text-[10px] tracking-widest uppercase text-emerald-400">Goal weight (kg)</Label>
            <Input
              data-testid="weight-goal-input"
              type="number"
              min={0}
              step={0.1}
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              placeholder="e.g. 78"
              className="mt-1 bg-neutral-900 border-neutral-800 text-white rounded-none h-10"
            />
          </div>
          <Button
            data-testid="weight-goal-save"
            type="button"
            onClick={() => saveGoal(false)}
            disabled={goalSaving}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-none uppercase tracking-wider font-display h-10"
          >
            {goalSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save
          </Button>
          {goalWeight != null && (
            <Button
              data-testid="weight-goal-clear"
              type="button"
              onClick={() => saveGoal(true)}
              disabled={goalSaving}
              variant="outline"
              className="bg-transparent border-neutral-700 text-white hover:bg-neutral-800 rounded-none h-10"
            >
              Clear
            </Button>
          )}
          <button
            data-testid="weight-goal-cancel"
            type="button"
            onClick={() => { setGoalEditing(false); setGoalInput(""); }}
            disabled={goalSaving}
            className="h-10 w-10 flex items-center justify-center text-neutral-400 hover:text-white"
            aria-label="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Chart */}
      <div className="relative border border-neutral-800 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent p-3 h-80 sm:h-96">
        {loading ? (
          <div className="h-full flex items-center justify-center text-neutral-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-neutral-500 text-sm gap-1 text-center px-4">
            <Flame className="w-8 h-8 text-neutral-700" />
            {sorted.length === 0 ? (
              <div>No entries yet — log your first weight to get the line moving.</div>
            ) : (
              <div>
                No entries in the last {view === "weekly" ? "7 days" : "30 days"}.<br />
                Log today's weight to start the line.
              </div>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 16, right: 12, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="weightFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="label" stroke="#525252" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis domain={yDomain} stroke="#525252" tickLine={false} axisLine={false} fontSize={11} width={40} unit="kg" />
              <Tooltip
                cursor={{ stroke: "#3b82f6", strokeOpacity: 0.4, strokeDasharray: "3 3" }}
                wrapperStyle={{ outline: "none", zIndex: 20 }}
                content={(props) => <RichTooltip {...props} goalWeight={goalWeight} />}
              />
              {insights && (
                <ReferenceLine
                  y={insights.lowest}
                  stroke="#facc15"
                  strokeDasharray="3 3"
                  strokeOpacity={0.55}
                  label={{ value: `Lowest ${insights.lowest}kg`, fill: "#facc15", fontSize: 10, position: "insideTopRight" }}
                />
              )}
              {goalWeight != null && goalWeight > 0 && (
                <ReferenceLine
                  y={goalWeight}
                  stroke="#10b981"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                  label={{ value: `Goal ${goalWeight}kg`, fill: "#10b981", fontSize: 11, fontWeight: 700, position: "insideBottomRight" }}
                />
              )}
              <Area
                type="monotone"
                dataKey="weight"
                stroke="#3b82f6"
                strokeWidth={3}
                fill="url(#weightFill)"
                style={{ filter: "url(#lineGlow)" }}
                dot={{ fill: "#3b82f6", stroke: "#fff", strokeWidth: 1, r: 4 }}
                activeDot={{
                  r: 7,
                  fill: "#fff",
                  stroke: "#3b82f6",
                  strokeWidth: 3,
                  style: { cursor: "pointer" },
                }}
                isAnimationActive
                animationDuration={600}
              />
              {liveMarkerKey && (
                <ReferenceDot
                  x={liveMarkerKey}
                  y={chartData.find((d) => d.label === liveMarkerKey)?.weight ?? 0}
                  r={9}
                  fill="#fff"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  isFront
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {chartData.length > 0 && (
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2 text-right">
          {view === "weekly" ? "Last 7 days" : "Last 30 days"} · {chartData.length} {chartData.length === 1 ? "entry" : "entries"}
        </div>
      )}
    </div>
  );
}

function InsightCell({ label, value, unit = "", tone = "flat", icon: Icon, testid }) {
  const color = tone === "down" ? "text-blue-500" : tone === "up" ? "text-amber-400" : tone === "goal" ? "text-emerald-400" : "text-white";
  const TrendIcon = tone === "down" ? TrendingDown : tone === "up" ? TrendingUp : null;
  return (
    <div data-testid={testid} className="border border-neutral-800 bg-neutral-900/50 p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`font-display text-xl mt-0.5 flex items-baseline gap-1 ${color}`}>
        {value}
        {unit && <span className="text-[10px] text-neutral-500">{unit}</span>}
        {TrendIcon && <TrendIcon className={`w-3.5 h-3.5 ${color}`} />}
      </div>
    </div>
  );
}

/**
 * RichTooltip
 * -----------
 * Custom Recharts Tooltip content. Replaces the default "weight: X" line
 * with a richer card that shows the full date, weight, delta from the
 * previous plotted entry (with up/down arrow), and distance to goal.
 *
 * Why a Recharts Tooltip instead of a separate pin-popover?
 *   - Recharts already implements the tap-to-stick behavior on touch
 *     devices natively — the tooltip stays visible after a tap until the
 *     user taps elsewhere on the chart.
 *   - It positions itself cleanly relative to the dot and flips at the
 *     right edge automatically, so we don't have to hand-calculate
 *     overflow/flip logic.
 *   - On desktop, hover still works for quick scrubbing along the line.
 */
function RichTooltip({ active, payload, goalWeight }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  if (!p) return null;

  const dateLabel = (() => {
    try {
      const d = new Date(p.date + "T00:00:00Z");
      return d.toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return p.date;
    }
  })();

  const delta = p.deltaKg;
  const deltaPositive = delta != null && delta > 0;
  const deltaNegative = delta != null && delta < 0;
  const toGoal = goalWeight != null && goalWeight > 0
    ? Number((p.weight - goalWeight).toFixed(1))
    : null;

  return (
    <div
      data-testid="weight-tooltip-card"
      className="bg-neutral-950/95 border border-blue-500/60 shadow-lg shadow-blue-500/10 backdrop-blur p-2.5"
      style={{ width: 168 }}
    >
      <div className="text-[10px] uppercase tracking-widest text-neutral-400 leading-tight mb-1">
        {dateLabel}
      </div>
      <div className="font-display text-2xl text-white flex items-baseline gap-1 leading-none">
        {p.weight}
        <span className="text-[10px] text-neutral-500">kg</span>
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1 text-[10px] uppercase tracking-widest">
        <div>
          <div className="text-neutral-500">vs prev</div>
          {delta == null ? (
            <div className="text-neutral-600">—</div>
          ) : (
            <div className={`font-display flex items-center gap-0.5 ${deltaNegative ? "text-emerald-400" : deltaPositive ? "text-blue-400" : "text-white"}`}>
              {deltaPositive ? "+" : ""}{delta}<span className="text-[9px] text-neutral-500">kg</span>
              {deltaNegative && <TrendingDown className="w-3 h-3" />}
              {deltaPositive && <TrendingUp className="w-3 h-3" />}
            </div>
          )}
        </div>
        <div>
          <div className="text-neutral-500">to goal</div>
          {toGoal == null ? (
            <div className="text-neutral-600">—</div>
          ) : (
            <div className={`font-display ${toGoal === 0 ? "text-emerald-400" : toGoal > 0 ? "text-blue-400" : "text-emerald-400"}`}>
              {toGoal > 0 ? "-" : toGoal < 0 ? "+" : ""}{Math.abs(toGoal)}<span className="text-[9px] text-neutral-500">kg</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
